import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserIcon, 
  EnvelopeIcon, 
  LockClosedIcon, 
  ShieldCheckIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  EnvelopeOpenIcon
} from '@heroicons/react/24/outline';
import OTPVerification from './OTPVerification';
import axios from 'axios';
import toast from 'react-hot-toast';
import { authAPI } from '../../services/api';
import { ageFromBirthDate, birthDateBounds, birthDateError } from '../../utils/age';

const Register = () => {
  const [step, setStep] = useState(1); // 1: Registration form, 2: OTP verification
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    birth_date: '',
    region: '',
    degree: '',
    job_role: '',
    role: 'user'
  });

  const degreeOptions = ['MD', 'MBBS', 'DO', 'PhD', 'MD-PhD', 'MS', 'MPH', 'RN', 'NP', 'PA', 'MBA', 'Other'];
  const jobOptions = [
    'Transplant Surgeon', 'Transplant Nephrologist', 'Transplant Coordinator',
    'Clinician / Physician', 'Nurse', 'Researcher', 'Data Scientist / Analyst',
    'Student / Trainee', 'Other'
  ];
  const usStates = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','District of Columbia','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { register } = useAuth();
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (formData.username.length > 20) {
      newErrors.username = 'Username must be less than 20 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    const birthError = birthDateError(formData.birth_date);
    if (birthError) newErrors.birth_date = birthError;
    if (!formData.region.trim()) {
      newErrors.region = 'Region is required';
    }
    if (!formData.degree) {
      newErrors.degree = 'Degree is required';
    }
    if (!formData.job_role) {
      newErrors.job_role = 'Job role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleRequestOTP = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.requestOTP({
        email: formData.email,
        username: formData.username
      });

      if (response.data.success) {
        setStep(2);
        toast.success('Verification code sent to your email');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerification = async (otp) => {
    setLoading(true);
    try {
      // Verify OTP first
      const verifyResponse = await authAPI.verifyOTP({
        email: formData.email,
        otp
      });

      if (verifyResponse.data.success) {
        // Proceed with registration
        const registerResult = await register(formData);
        
        if (registerResult.success) {
          toast.success('Registration successful! Redirecting to login...');
          setTimeout(() => navigate('/login'), 2000);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      const response = await authAPI.resendOTP({
        email: formData.email,
        username: formData.username
      });

      if (response.data.success) {
        toast.success('New verification code sent');
      }
    } catch (error) {
      toast.error('Failed to resend code');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-4">
      <AnimatePresence mode="wait">
        {step === 1 ? (
          <motion.div
            key="register-form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="max-w-md w-full space-y-4 bg-white p-5 rounded-2xl shadow-xl"
          >
            <div>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="mx-auto h-20 w-20 bg-gradient-to-r from-primary-600 to-primary-400 rounded-2xl flex items-center justify-center"
              >
                <span className="text-white text-2xl font-bold">AIQ</span>
              </motion.div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                Create Account
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                Or{' '}
                <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                  sign in to existing account
                </Link>
              </p>
            </div>

            <form className="mt-8 space-y-6" onSubmit={(e) => e.preventDefault()}>
              <div className="space-y-4">
                {/* Username Field */}
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="username"
                      name="username"
                      type="text"
                      required
                      value={formData.username}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border ${
                        errors.username ? 'border-red-500' : 'border-gray-300'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200`}
                      placeholder="johndoe"
                    />
                  </div>
                  {errors.username && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-1 text-sm text-red-600"
                    >
                      {errors.username}
                    </motion.p>
                  )}
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200`}
                      placeholder="you@example.com"
                    />
                  </div>
                  {errors.email && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-1 text-sm text-red-600"
                    >
                      {errors.email}
                    </motion.p>
                  )}
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border ${
                        errors.password ? 'border-red-500' : 'border-gray-300'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200`}
                      placeholder="••••••••"
                    />
                  </div>
                  {errors.password && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-1 text-sm text-red-600"
                    >
                      {errors.password}
                    </motion.p>
                  )}
                 
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border ${
                        errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200`}
                      placeholder="••••••••"
                    />
                  </div>
                  {errors.confirmPassword && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-1 text-sm text-red-600"
                    >
                      {errors.confirmPassword}
                    </motion.p>
                  )}
                </div>

                {/* Date of birth (age is derived from it, so it stays current) */}
                <div>
                  <label htmlFor="birth_date" className="block text-sm font-medium text-gray-700 mb-1">Date of birth</label>
                  <input id="birth_date" name="birth_date" type="date" required
                    {...birthDateBounds()}
                    value={formData.birth_date} onChange={handleChange}
                    className={`w-full px-4 py-3 border ${errors.birth_date ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`} />
                  {errors.birth_date ? (
                    <p className="mt-1 text-sm text-red-600">{errors.birth_date}</p>
                  ) : ageFromBirthDate(formData.birth_date) !== null && (
                    <p className="mt-1 text-xs text-gray-500">Age {ageFromBirthDate(formData.birth_date)}</p>
                  )}
                </div>

                {/* State */}
                <div>
                  <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <select id="region" name="region" required value={formData.region} onChange={handleChange}
                    className={`w-full px-4 py-3 border ${errors.region ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}>
                    <option value="">Select state…</option>
                    {usStates.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.region && <p className="mt-1 text-sm text-red-600">{errors.region}</p>}
                </div>

                {/* Degree */}
                <div>
                  <label htmlFor="degree" className="block text-sm font-medium text-gray-700 mb-1">Degree</label>
                  <select id="degree" name="degree" required value={formData.degree} onChange={handleChange}
                    className={`w-full px-4 py-3 border ${errors.degree ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}>
                    <option value="">Select degree…</option>
                    {degreeOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  {errors.degree && <p className="mt-1 text-sm text-red-600">{errors.degree}</p>}
                </div>

                {/* Job role */}
                <div>
                  <label htmlFor="job_role" className="block text-sm font-medium text-gray-700 mb-1">Job role</label>
                  <select id="job_role" name="job_role" required value={formData.job_role} onChange={handleChange}
                    className={`w-full px-4 py-3 border ${errors.job_role ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}>
                    <option value="">Select role…</option>
                    {jobOptions.map((j) => <option key={j} value={j}>{j}</option>)}
                  </select>
                  {errors.job_role && <p className="mt-1 text-sm text-red-600">{errors.job_role}</p>}
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={handleRequestOTP}
                  disabled={loading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {loading ? (
                    <>
                      <ArrowPathIcon className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                      Sending verification...
                    </>
                  ) : (
                    <>
                      <EnvelopeOpenIcon className="h-5 w-5 mr-2" />
                      Continue with Email Verification
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className="mt-4 text-xs text-center text-gray-500">
              By signing up, you agree to our{' '}
              <a href="#" className="text-primary-600 hover:text-primary-500">Terms</a>{' '}
              and{' '}
              <a href="#" className="text-primary-600 hover:text-primary-500">Privacy Policy</a>
            </div>
          </motion.div>
        ) : (
          <OTPVerification
            key="otp-verification"
            email={formData.email}
            onVerify={handleOTPVerification}
            onResend={handleResendOTP}
            onBack={() => setStep(1)}
            loading={loading}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Register;